import { AudioRingBuffer } from './audio-ring-buffer';

/**
 * Pre-roll: quanto de áudio ANTES do disparo do VAD entra na janela extraída.
 * É o que impede o corte da primeira sílaba — o detector só conclui "isto é fala" depois de
 * a fala já ter começado, então a janela tem de olhar para trás.
 */
export const PRE_ROLL_MS = 300;

/** Converte milissegundos da régua do buffer em índice ABSOLUTO de amostra (clampado em 0). */
export function amostraDeMs(ms: number, taxaHz: number): number {
  return Math.max(0, Math.round((ms * taxaHz) / 1000));
}

/** Inverso de {@link amostraDeMs}: índice absoluto de amostra → milissegundos. */
export function msDeAmostra(amostra: number, taxaHz: number): number {
  return (amostra / taxaHz) * 1000;
}

/**
 * Régua temporal da captura: **o ring buffer É o relógio**.
 *
 * Por que não `AudioContext.currentTime`: o contexto começa a contar no instante em que é
 * criado, mas o worklet só passa a ESCREVER depois de `audioWorklet.addModule()` (um fetch de
 * rede/service worker), da criação do nó e do `connect`. Chame T0 esse intervalo. Usando
 * `currentTime`, a amostra de índice `k` contém o áudio de `T0 + k/taxa` — a janela extraída
 * sai deslocada e o pre-roll de 300 ms encolhe ou desaparece conforme a latência da rede
 * (com T0 ≥ 300 ms a extração passa a começar DEPOIS do disparo, que é justamente o bug do
 * corte da primeira sílaba, agora intermitente).
 *
 * Derivando o tempo da posição do buffer, o mesmo contador serve de origem para a decisão do
 * VAD e para a extração. Isso imuniza, **por construção**, contra:
 *  - T0 (o instante zero é a primeira amostra escrita, não a criação do contexto);
 *  - atraso da fila de mensagens do worklet (a régua anda quando o áudio chega, não antes);
 *  - quadros perdidos e suspensão do contexto (o relógio para junto com o áudio).
 *
 * O erro residual passa a errar para ANTES do disparo — o lado seguro, absorvido pelo pre-roll.
 */
export class AudioTimeline {
  constructor(
    readonly ring: AudioRingBuffer,
    readonly taxaHz: number,
  ) {}

  /** "Agora" na régua do buffer: total de amostras escritas convertido em ms. */
  get agoraMs(): number {
    return msDeAmostra(this.ring.posicao, this.taxaHz);
  }

  escrever(frame: Float32Array): void {
    this.ring.escrever(frame);
  }

  /**
   * Janela `[de, ate)` em amostras absolutas para o intervalo pedido, já com o pre-roll
   * subtraído do início. Mesma aritmética que produz `agoraMs`, no sentido inverso.
   */
  janela(inicioMs: number, fimMs: number, preRollMs = PRE_ROLL_MS): { de: number; ate: number } {
    return {
      de: amostraDeMs(inicioMs - preRollMs, this.taxaHz),
      ate: amostraDeMs(fimMs, this.taxaHz),
    };
  }

  /** PCM cru da janela pedida (com pre-roll). Vazio se a janela já saiu do buffer. */
  extrair(inicioMs: number, fimMs: number, preRollMs = PRE_ROLL_MS): Float32Array {
    const { de, ate } = this.janela(inicioMs, fimMs, preRollMs);
    return this.ring.extrair(de, ate);
  }
}
